import {
  FC,
  SyntheticEvent,
  useState,
  useMemo,
  useRef,
  useEffect,
} from "react";
import { useFormContext, Controller } from "react-hook-form";
import { TextField, Autocomplete, CircularProgress } from "@mui/material";
import { debounce } from "lodash";
import __ from "src/utils/Translations";
import useFetchAreasData from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/OrganizationStructure/api/useFetchAreasData";
import { AxiosError } from "axios";
import { useLogStatus } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus";
import { NewRelicLogLevel } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus.models";
import { useParams } from "react-router-dom";

interface IAutocompleteAdditionalArea {
  companyId: string;
  structureId: string;
  dataTestId?: string;
}

const AutocompleteAdditionalArea: FC<IAutocompleteAdditionalArea> = ({
  companyId,
  structureId,
  dataTestId,
}) => {
  const { control } = useFormContext();
  const [search, setSearch] = useState("");

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    error: areasError,
  } = useFetchAreasData(companyId, structureId, search);

  const logStatus = useLogStatus();

  if (areasError) {
    logStatus({
      type: NewRelicLogLevel.ERROR,
      message: "AutocompleteAdditionalArea: fetchAreasData failed",
      statusDetails: {
        source: "API",
        operation: "fetchAreasData",
        status: (areasError as AxiosError)?.response?.status,
        url: (areasError as AxiosError)?.config?.url,
      },
    });
  }

  const { areaId: excludedValue } = useParams<{ areaId: string }>();

  const options = useMemo(
    () =>
      (data?.pages.flatMap((page) => page.content) || []).filter(
        (option) => option.id !== excludedValue
      ),
    [data?.pages, excludedValue]
  );

  const debouncedSearch = useRef(
    debounce((_: SyntheticEvent, value: string) => {
      setSearch(value);
    }, 300)
  ).current;

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const handleScroll = (event: SyntheticEvent) => {
    const listbox = event.currentTarget;
    if (
      (listbox.scrollTop + listbox.clientHeight) / listbox.scrollHeight > 0.9 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  };

  return (
    <Controller
      name="additionalDisplayArea"
      control={control}
      render={({ field: { onChange, value }, fieldState }) => (
        <Autocomplete
          options={options}
          disableListWrap
          disablePortal
          value={value || null}
          onChange={(_, newValue) => onChange(newValue)}
          onInputChange={(event, newInputValue, reason) => {
            if (reason !== "reset") {
              debouncedSearch(event, newInputValue);
            }
          }}
          onOpen={() => setSearch("")}
          filterOptions={(options) => options}
          noOptionsText={__("Brak wyników wyszukiwania")}
          renderInput={(params) => (
            <TextField
              {...params}
              data-testId={dataTestId}
              label={__("Wyświetlany dodatkowy obszar")}
              error={fieldState.invalid}
              helperText={
                fieldState.error?.message ??
                __(
                  "Wyświetlany dodatkowy obszar będzie pokazany na karcie pracownika jako miejsce jego przynależności w strukturze organizacji obok obszaru, do którego należy. Przykład: Zespół kadr, Kadry i płace."
                )
              }
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps?.input,
                  endAdornment: (
                    <>
                      {isFetching && (
                        <CircularProgress color="inherit" size={20} />
                      )}
                      {(params.slotProps?.input as any)?.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
          slotProps={{
            listbox: {
              onScroll: handleScroll,
              style: { maxHeight: "300px", overflowY: "auto" },
            },
          }}
        />
      )}
    />
  );
};

export default AutocompleteAdditionalArea;
